import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ClipboardList, Search, Calendar, Filter, ArrowLeft, Sparkles, ArrowRight,
  Phone, MapPin, Users, Mail, MessageCircle, Share2, Copy, Check,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/patient-log';
const SHARE_MESSAGE = (
  `New on DocSite — one tab to track every patient you saw today.\n\n` +
  `Search by name, phone last-4 or what happened. Filter by where they came from. ` +
  `Auto-pulls online bookings + Connect-page submissions + call requests into the same view.\n\n` +
  `Read more: ${SHARE_URL}`
);

export default function PatientLogLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent("DocSite — your new Patient Log")}&body=${encodeURIComponent(SHARE_MESSAGE)}`;
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'DocSite — your new Patient Log', text: SHARE_MESSAGE, url: SHARE_URL }); }
      catch { /* cancelled */ }
    } else { copyLink(); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>DocSite — One tab to track every patient you saw today</title>
        <meta name="description" content="The new DocSite Patient Log gives you one chronological list of every patient who showed up — walk-in, online booking, Connect-page lead, phone call. Search by name, phone, what happened. Filter by where they came from." />
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="doctor patient log India, daily patient list, clinic CRM, doctor visit tracker, manage patient flow, doctor appointment record, DocSite patient log" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="DocSite — One tab to track every patient you saw today" />
        <meta property="og:description" content="One chronological log merging walk-ins, online bookings, Connect-page leads and phone calls. Search by name or phone, filter by source." />
        <meta property="og:url" content={SHARE_URL} />
        <meta property="article:published_time" content="2026-02-12" />
        <link rel="canonical" href={SHARE_URL} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'NewsArticle',
          headline: 'One tab to track every patient you saw today',
          description: 'A unified daily-log for every patient interaction — manually logged or auto-imported from Connect submissions, online bookings and call requests.',
          datePublished: '2026-02-12', dateModified: '2026-02-12',
          author: { '@type': 'Organization', name: 'DocSite', url: 'https://docsite.care' },
          publisher: { '@type': 'Organization', name: 'DocSite', url: 'https://docsite.care',
            logo: { '@type': 'ImageObject', url: 'https://docsite.care/logo512.png' } },
          mainEntityOfPage: SHARE_URL,
        })}</script>
      </Helmet>

      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
        <Link to="/whats-new" data-testid="patientlog-launch-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft size={16} /> All updates
        </Link>

        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Hero */}
          <div className="bg-gradient-to-br from-slate-900 via-sky-800 to-emerald-700 text-white p-6 sm:p-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-bold uppercase tracking-widest mb-4">
              <Sparkles size={14} /> Just Launched · 12 Feb 2026
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              One tab to track every patient you saw today.
            </h1>
            <p className="text-slate-100 mt-3 text-sm sm:text-base leading-relaxed">
              The new <b>Patient Log</b> is your daily operating screen. Walk-ins, online bookings,
              Connect-page leads and phone calls all land on the same chronological list. Default
              view is the last 3 days; pull older entries any time with one filter tap.
            </p>
          </div>

          {/* Pills */}
          <div className="p-6 sm:p-8 space-y-6">
            <div className="grid sm:grid-cols-3 gap-3">
              <Pill icon={<ClipboardList size={18} className="text-sky-600" />} title="One unified list" body="Walk-ins + online bookings + Connect leads + call requests, all in one place." testid="patientlog-pill-unified" />
              <Pill icon={<Search size={18} className="text-emerald-600" />} title="Find any patient" body="Search by name, the last 4 of their phone, what happened, or your notes." testid="patientlog-pill-search" />
              <Pill icon={<Filter size={18} className="text-amber-600" />} title="Sort by source" body="Filter by where they came from — walk-in, QR scan, online booking, hospital referral, doctor referral…" testid="patientlog-pill-filter" />
            </div>

            {/* What you fill in */}
            <div className="rounded-2xl border-2 border-sky-200 bg-sky-50 p-5">
              <p className="text-[10px] font-bold text-sky-700 uppercase tracking-widest mb-2">When you log a visit, you fill in…</p>
              <ul className="space-y-2 text-sm text-slate-800">
                <li className="flex items-start gap-2">
                  <Users size={16} className="text-sky-700 mt-0.5 shrink-0" />
                  <span><b>Patient name + phone</b> — the phone is encrypted at rest; only the last 4 digits show on the list.</span>
                </li>
                <li className="flex items-start gap-2">
                  <Calendar size={16} className="text-sky-700 mt-0.5 shrink-0" />
                  <span><b>Visit date</b> — defaults to today.</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin size={16} className="text-sky-700 mt-0.5 shrink-0" />
                  <span><b>Came via</b> — pick from 9 channels: <i>Online / WhatsApp · My Website · Poly / Clinic walk-in · Hospital walk-in · My referral network · Ref by Doctor / Hospital / Poly-Clinic / Patient</i>. Referrer name shows up for any &quot;Ref by…&quot; option.</span>
                </li>
                <li className="flex items-start gap-2">
                  <Phone size={16} className="text-sky-700 mt-0.5 shrink-0" />
                  <span><b>What happened</b> + <b>Notes</b> — free text. Searchable.</span>
                </li>
              </ul>
              <p className="text-xs text-sky-800 mt-3">Total: under 30 seconds per visit. Most doctors log between consults or once at the end of the day.</p>
            </div>

            {/* Real-life scenarios */}
            <div className="space-y-3">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">How this helps your day</p>
              <Scenario
                icon={<ClipboardList size={18} className="text-sky-600" />}
                title="Closing the day, in one screen"
                body="Open the tab at 7 PM. You see every patient who came in today, every patient who booked online for tomorrow, every Connect-page lead from this week, and every phone call. No more switching between three different lists."
                testid="patientlog-scen-day-close"
              />
              <Scenario
                icon={<Search size={18} className="text-emerald-600" />}
                title="When a patient calls back next month"
                body="They say &quot;Doctor, you saw me last month — surname Sharma, I had ear pain.&quot; Type &quot;Sharma&quot; or the last 4 of their phone in the search bar. Their entire visit history pops up in under a second."
                testid="patientlog-scen-callback"
              />
              <Scenario
                icon={<Filter size={18} className="text-amber-600" />}
                title="Knowing which channels actually feed you patients"
                body="Filter by &quot;Online / WhatsApp&quot; for last 30 days. Count the rows. Compare to &quot;Hospital walk-in&quot;. Now you know exactly which channels deserve your time, your money, your follow-up posters. Data-driven, not guess-driven."
                testid="patientlog-scen-channels"
              />
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Link to="/dashboard" data-testid="patientlog-launch-cta-open"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm shadow-sm">
                Open my Patient Log <ArrowRight size={16} />
              </Link>
              <Link to="/whats-new" data-testid="patientlog-launch-cta-allupdates"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm">
                See all updates
              </Link>
            </div>

            {/* Share */}
            <div className="pt-4 border-t border-slate-200">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Tell a colleague</p>
              <div className="flex flex-wrap gap-2">
                <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="patientlog-launch-share-whatsapp"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200">
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a href={mailHref} data-testid="patientlog-launch-share-email"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-semibold border border-sky-200">
                  <Mail size={14} /> Email
                </a>
                <button onClick={nativeShare} data-testid="patientlog-launch-share-native"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  <Share2 size={14} /> Share
                </button>
                <button onClick={copyLink} data-testid="patientlog-launch-share-copy"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                </button>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          <ClipboardList size={12} className="inline mr-1" />Find this in your Dashboard → second tab after Profile.
        </p>
      </div>
    </div>
  );
}

function Pill({ icon, title, body, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">{icon}<p className="text-sm font-semibold text-slate-900">{title}</p></div>
      <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function Scenario({ icon, title, body, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
